import { List, useTable } from '@refinedev/antd';
import { Table, Space, Tag, Image, Button, Modal, Input, message } from 'antd';
import { CheckOutlined, CloseOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';

interface Document {
  id: number;
  userId: number;
  type: string;
  fileUrl: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectedNote: string | null;
  createdAt: string;
  user: {
    fullName: string;
    phone: string;
  };
}

const documentTypeLabels: Record<string, string> = {
  DRIVER_LICENSE_FRONT: 'Haydovchilik guvohnomasi',
  DRIVER_SELFIE: 'Selfie',
  VEHICLE_REGISTRATION: 'Texnik passport',
  VEHICLE_PHOTO_FRONT: 'Avtomobil (old)',
  VEHICLE_PHOTO_BACK: 'Avtomobil (orqa)',
  VEHICLE_PHOTO_LEFT: 'Avtomobil (chap)',
  VEHICLE_PHOTO_RIGHT: 'Avtomobil (o\'ng)',
  VEHICLE_PHOTO_INTERIOR: 'Avtomobil ichki',
};

export const DocumentUserDocuments = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const { tableProps, tableQueryResult } = useTable<Document>({
    resource: 'admin/documents',
    syncWithLocation: false,
    filters: {
      permanent: [{ field: 'userId', operator: 'eq', value: userId }],
    },
    pagination: {
      pageSize: 50,
    },
  });

  const userName = tableQueryResult.data?.data?.[0]?.user?.fullName || '';
  const userPhone = tableQueryResult.data?.data?.[0]?.user?.phone || '';

  const handleApprove = async (doc: Document) => {
    try {
      await apiClient.put(`/admin/documents/${doc.id}/review`, {
        status: 'APPROVED',
      });
      message.success('Hujjat tasdiqlandi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleReject = async () => {
    if (!selectedDoc) return;
    try {
      await apiClient.put(`/admin/documents/${selectedDoc.id}/review`, {
        status: 'REJECTED',
        note: rejectNote,
      });
      message.success('Hujjat rad etildi');
      setRejectModalVisible(false);
      setSelectedDoc(null);
      setRejectNote('');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    {
      title: 'Rasm',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      width: 100,
      render: (url: string) => (
        <Image
          src={url}
          alt="Document"
          width={80}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    {
      title: 'Hujjat turi',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => documentTypeLabels[type] || type,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag
          color={
            status === 'APPROVED' ? 'green' : status === 'REJECTED' ? 'red' : 'orange'
          }
        >
          {status === 'APPROVED'
            ? 'Tasdiqlangan'
            : status === 'REJECTED'
              ? 'Rad etilgan'
              : 'Kutilmoqda'}
        </Tag>
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: Document) =>
        record.status === 'PENDING' && (
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="small"
              onClick={() => handleApprove(record)}
            >
              Tasdiqlash
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              size="small"
              onClick={() => {
                setSelectedDoc(record);
                setRejectModalVisible(true);
              }}
            >
              Rad etish
            </Button>
          </Space>
        ),
    },
  ];

  const title = userName
    ? `${userName} — ${userPhone}`
    : `Foydalanuvchi #${userId} hujjatlari`;

  return (
    <List
      title={title}
      headerButtons={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/documents')}
        >
          Orqaga
        </Button>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" pagination={false} />

      <Modal
        title="Rad etish sababi"
        open={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => {
          setRejectModalVisible(false);
          setSelectedDoc(null);
          setRejectNote('');
        }}
        okText="Rad etish"
        cancelText="Bekor qilish"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          placeholder="Rad etish sababini kiriting..."
          rows={4}
        />
      </Modal>
    </List>
  );
};
